import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { colors, breakpoints, fadeIn } from '../../../assets/styles/GlobalStyles';
import { consultaProtocolosService } from '../services/consultaProtocolosService';
import { FaArrowLeft, FaSearch, FaTimes, FaSync } from 'react-icons/fa';
import MainLayout from '../../../layouts/MainLayout';
import ProtocoloAndamentoCard from '../components/ProtocoloAndamentoCard';
import ProtocolosSkeleton from '../components/ProtocolosSkeleton';
import ProtocolosError from '../components/ProtocolosError';
import EmptyProtocolos from '../components/EmptyProtocolos';
import StatusLegend from '../components/StatusLegend';

const PageContainer = styled.div`
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 300px;
    background: linear-gradient(135deg, ${colors.blueDark} 0%, ${colors.blueMedium} 100%);
    opacity: 0.05;
    pointer-events: none;
    z-index: 0;
  }
`;

const ProtocolosContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 120px 2rem 2rem 2rem;
  animation: ${fadeIn} 0.5s ease-out;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 100px 1.5rem 1.5rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 90px 1rem 1rem 1rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  color: ${colors.darkBlue};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 1.5rem;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  svg {
    font-size: 1rem;
    transition: transform 0.2s ease;
  }

  &:hover {
    color: ${colors.lightBlue};

    svg {
      transform: translateX(-3px);
    }
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a2c3e;
  margin-bottom: 8px;

  @media (max-width: 480px) {
    font-size: 1.4rem;
  }
`;

const PageSubtitle = styled.p`
  color: #5a6e7c;
  margin-bottom: 32px;
  font-size: 0.9rem;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${colors.backgroundLight};
  border: 1px solid ${colors.borderLight};
  color: ${colors.darkBlue};
  font-size: 0.8rem;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 100px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-top: 4px;

  svg {
    font-size: 0.85rem;
    animation: ${props => props.$spinning ? spin : 'none'} 0.8s linear infinite;
  }

  &:hover {
    background: ${colors.lightBlue}15;
    border-color: ${colors.lightBlue};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const ProtocolosList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ProtocolosCount = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
  font-size: 0.85rem;
  color: #5a6e7c;

  strong {
    color: ${colors.darkBlue};
    margin-right: 4px;
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 1px solid #eef2f6;
`;

const TabButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => props.$active ? colors.darkBlue : 'transparent'};
  padding: 0 4px 12px 4px;
  margin-right: 24px;

  @media (max-width: 480px) {
    margin-right: 0;
  }
  font-size: 0.95rem;
  font-weight: 600;
  color: ${props => props.$active ? colors.darkBlue : '#5a6e7c'};
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  &:hover {
    color: ${colors.darkBlue};
  }
`;

const SearchBox = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const SearchIcon = styled(FaSearch)`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.85rem;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 40px 12px 38px;
  border: 1px solid ${colors.borderLight};
  border-radius: 12px;
  font-size: 0.9rem;
  background: ${colors.white};
  color: #1a2c3e;
  box-sizing: border-box;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  transition: all 0.2s ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: ${colors.lightBlue};
    box-shadow: 0 0 0 3px ${colors.lightBlue}22;
  }

  // 16px evita o zoom automático do iOS ao focar
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 16px;
  }
`;

const ClearSearchButton = styled.button`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  padding: 4px;

  &:hover {
    color: #5a6e7c;
  }
`;

const TabCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 700;
  background: ${props => props.$active ? colors.darkBlue : colors.backgroundLight};
  color: ${props => props.$active ? colors.white : colors.textMuted};
`;

const useProtocolos = () => {
  const [protocolos, setProtocolos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // silent=true é usado no botão "Atualizar": refaz a busca sem trocar a tela
  // inteira pro skeleton, já que os dados antigos continuam válidos até a
  // resposta nova chegar.
  const loadProtocolos = async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await consultaProtocolosService.getProtocolos();

      if (response.success) {
        setProtocolos(response.data);
      } else {
        setError(response.error || 'Erro ao carregar protocolos');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar protocolos');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadProtocolos();
  }, []);

  return { protocolos, loading, refreshing, error, loadProtocolos };
};

const ConsultaProtocolosPage = () => {
  const navigate = useNavigate();
  const { protocolos, loading, refreshing, error, loadProtocolos } = useProtocolos();
  const [activeTab, setActiveTab] = useState('andamento');
  const [busca, setBusca] = useState('');

  const handleGoBack = () => navigate('/home');

  if (loading) {
    return (
      <MainLayout>
        <PageContainer>
          <ProtocolosContainer>
            <BackButton onClick={handleGoBack}>
              <FaArrowLeft /> Voltar para Home
            </BackButton>
            <PageTitle>Consulta de Protocolos</PageTitle>
            <PageSubtitle>Acompanhe o andamento dos seus protocolos</PageSubtitle>
            <ProtocolosSkeleton />
          </ProtocolosContainer>
        </PageContainer>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <PageContainer>
          <ProtocolosContainer>
            <BackButton onClick={handleGoBack}>
              <FaArrowLeft /> Voltar para Home
            </BackButton>
            <PageTitle>Consulta de Protocolos</PageTitle>
            <PageSubtitle>Acompanhe o andamento dos seus protocolos</PageSubtitle>
            <ProtocolosError error={error} onRetry={loadProtocolos} />
          </ProtocolosContainer>
        </PageContainer>
      </MainLayout>
    );
  }

  const emAndamento = (protocolos || []).filter((p) => !p.concluido);
  const concluidos = (protocolos || []).filter((p) => p.concluido);
  const listaAtiva = activeTab === 'andamento' ? emAndamento : concluidos;

  const termoBusca = busca.trim().toLowerCase();
  const listaFiltrada = termoBusca
    ? listaAtiva.filter((p) =>
        p.tipoPedido?.toLowerCase().includes(termoBusca) ||
        String(p.numeroProtocolo).toLowerCase().includes(termoBusca)
      )
    : listaAtiva;

  const hasProtocolos = listaFiltrada.length > 0;
  const buscaSemResultado = Boolean(termoBusca) && !hasProtocolos;

  return (
    <MainLayout>
      <PageContainer>
        <ProtocolosContainer>
          <BackButton onClick={handleGoBack}>
            <FaArrowLeft /> Voltar para Home
          </BackButton>

          <HeaderRow>
            <PageTitle>Consulta de Protocolos</PageTitle>
            <RefreshButton
              onClick={() => loadProtocolos({ silent: true })}
              disabled={refreshing}
              $spinning={refreshing}
            >
              <FaSync /> {refreshing ? 'Atualizando...' : 'Atualizar'}
            </RefreshButton>
          </HeaderRow>
          <PageSubtitle>Acompanhe o andamento dos seus protocolos</PageSubtitle>

          <Tabs>
            <TabButton $active={activeTab === 'andamento'} onClick={() => setActiveTab('andamento')}>
              Em Andamento
              <TabCount $active={activeTab === 'andamento'}>{emAndamento.length}</TabCount>
            </TabButton>
            <TabButton $active={activeTab === 'concluidos'} onClick={() => setActiveTab('concluidos')}>
              Concluídos
              <TabCount $active={activeTab === 'concluidos'}>{concluidos.length}</TabCount>
            </TabButton>
          </Tabs>

          <SearchBox>
            <SearchIcon />
            <SearchInput
              type="text"
              aria-label="Buscar protocolo por tipo de pedido ou número"
              placeholder="Buscar por tipo de pedido ou nº do protocolo"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
            {busca && (
              <ClearSearchButton onClick={() => setBusca('')} aria-label="Limpar busca">
                <FaTimes />
              </ClearSearchButton>
            )}
          </SearchBox>

          {activeTab === 'andamento' && <StatusLegend />}

          {hasProtocolos && (
            <ProtocolosCount>
              <strong>{listaFiltrada.length}</strong> protocolo(s) encontrado(s)
            </ProtocolosCount>
          )}

          <ProtocolosList>
            {hasProtocolos ? (
              listaFiltrada.map((protocolo) => (
                <ProtocoloAndamentoCard key={protocolo.id} protocolo={protocolo} />
              ))
            ) : buscaSemResultado ? (
              <EmptyProtocolos
                title="Nenhum resultado encontrado"
                message={`Não encontramos nenhum pedido para "${busca.trim()}".`}
              />
            ) : activeTab === 'andamento' ? (
              <EmptyProtocolos
                title="Nenhum protocolo em andamento"
                message="Você não possui protocolos em trâmite no momento."
              />
            ) : (
              <EmptyProtocolos
                title="Nenhum protocolo concluído"
                message="Você ainda não possui protocolos concluídos."
              />
            )}
          </ProtocolosList>
        </ProtocolosContainer>
      </PageContainer>
    </MainLayout>
  );
};

export default ConsultaProtocolosPage;
